import { Component, OnInit, signal, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { CommonSuperComponent } from '../../../../common.component.module/common.super';
import { AppState } from '../../../../app.state';
import { ErrorObservable, MessageObservable } from '../../../../services.module/message.observable';
import { ErrorModel, ErrorSeverity, MessageModel } from '../../../../model.module/modals.model';
import { ValidatorInput } from '../../../../model.module/validation/validation-descriptor.entity';
import { AppConstants } from '../../../../app-constants.service';
import { ConfigurationApiService } from '../../../../../generated/api/configuration/configuration.service';
import { UserApiService } from '../../../../../generated/api/user/user.service';
import { AuthService } from '../../../../services.module/auth.service';
import { NutzerAnlageResponse } from '../../../../../generated/api/kreditportalBackendAPI.schemas';
import { UserCreateModel } from '../../../../model.module/xhr/user-create.model';
import { take } from 'rxjs';

@Component({
    selector: 'benutzer-anlegen',
    templateUrl: './benutzer-anlegen.component.html',
    styleUrls: ['./benutzer-anlegen.component.less'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class BenutzerAnlegenComponent extends CommonSuperComponent implements OnInit {

    model: UserCreateModel = new UserCreateModel();

    nutzerAnlegenBlockiert = signal(true);

    constructor(
        protected override readonly appConstants: AppConstants,
        protected override readonly configurationApiService: ConfigurationApiService,
        protected readonly router: Router,
        protected readonly activatedRoute: ActivatedRoute,
        protected readonly state: AppState,
        protected readonly authService: AuthService,
        protected readonly messageHandler: MessageObservable,
        protected readonly errorHandler: ErrorObservable,
        protected readonly userApiService: UserApiService
    ) {
        super(appConstants, configurationApiService);
    }

    ngOnInit(): void {
        this.ermittleNeueNutzerId();
    }

    onFieldKeyUp(event: KeyboardEvent) {
        if (event.key === 'Enter') {
            this.benutzerAnlegen();
        }
    }

    validateField(field: ValidatorInput): void {
        this.model.validateField(field);
    }

    benutzerAnlegen(): void {
        const validationResult = this.model.validate();
        const valid = validationResult === null;

        if (!valid) {
            return;
        }

        this.model.vsnr = this.authService.getActiveKunde().vsnr;
        this.model.url = this.getUrl(this.appConstants.loginUrl + '?type=user&uuid=');

        this.nutzerAnlegenBlockiert.set(true);

        const nutzerAnlageRequest = this.model.forRequest();

        this.userApiService.starteNutzerAnlage(nutzerAnlageRequest)
            .pipe(take(1))
            .subscribe({
                next: res => {
                    if (!res.erfolg) {
                        this.handleError(res);
                        return;
                    }

                    this.messageHandler.notify(new MessageModel(
                        'Benutzer wurde angelegt',
                        [this.appConstants.messages.userAnlegenDetails],
                    ));

                    this.ermittleNeueNutzerId();

                    this.goBack();
                },
                error: (error) => {
                    console.log(error);
                    this.nutzerAnlegenBlockiert.set(false);
                }
            });
    }

    goBack() {
        this.router.navigate(
            ['./'],
            { relativeTo: this.activatedRoute, queryParams: { task: 'refreshList' } },
        );
    }

    private ermittleNeueNutzerId() {
        this.nutzerAnlegenBlockiert.set(true);

        const activeVsnr = this.authService.getActiveKunde().vsnr;

        this.userApiService.findUsersByVsnr(activeVsnr).subscribe({
            next: res => {
                let lastUserId: number = parseInt(res.userDataList[0].uid.value);

                for (const user of res.userDataList) {
                    if (parseInt(user.uid.value) > lastUserId) {
                        lastUserId = parseInt(user.uid.value);
                    }
                }

                const newUserId = lastUserId + 1;

                this.model.neueNutzerId = newUserId.toString();

                this.nutzerAnlegenBlockiert.set(false);
            },
            complete: () => {
                this.nutzerAnlegenBlockiert.set(false);
            }
        });
    }

    private handleError(res: NutzerAnlageResponse) {
        // Felder erfolgreich validiert, aber trotzdem erfolg=false -> Fehler
        if (res.email.validationResult && res.vorname.validationResult && res.nachname.validationResult) {
            this.errorHandler.notify(new ErrorModel(
                ErrorSeverity.SERIOUS,
                this.appConstants.messages.fehlerAufgetreten,
                ['Der Benutzer konnte aufgrund eines System-Fehlers nicht angelegt werden.'],
                undefined
            ));

            return;
        }

        this.model.mapResponse(res);
    }

}
